// src/stores/marketplace.js
import { defineStore } from 'pinia';
import api from '@/api';
import { useAuthStore } from '@/stores/auth';

// Build-time flag (Vue CLI replaces process.env.* in source at build)
const HIDE_GST =
  String(process.env.VUE_APP_HIDE_GST_INVOICE || 'false').toLowerCase() === 'true';

// Expose for console debugging: run `window.__HIDE_GST__` in DevTools
if (typeof window !== 'undefined') {
  window.__HIDE_GST__ = HIDE_GST;
}

function toBool(v) {
  return v === true || v === 1 || v === '1' || String(v).toLowerCase() === 'true';
}
function toNum(v, d = 0) {
  const n = Number(v);
  return Number.isFinite(n) ? n : d;
}

function normalizeTool(raw = {}) {
  const isPaidRaw = raw.is_paid ?? raw.isPaid ?? raw.paid;
  return {
    id: raw.id ?? raw.toolId ?? null,
    name: raw.name || '',
    description: raw.description || '',
    category: raw.category || '',
    iconUrl: raw.icon_url ?? raw.iconUrl ?? '/assets/icons/default.svg',
    launchRoute: raw.launch_route ?? raw.launchRoute ?? '',
    isPaid: toBool(isPaidRaw),
    cost:
      toNum(raw.price_in_credits) ??
      toNum(raw.priceInCredits) ??
      toNum(raw.cost, 0),
    _raw: raw
  };
}

function hasGstTool(tools = []) {
  return tools.some((t) => isGstTool(t));
}

// Robust matcher for the GST invoice tool
function isGstTool(t = {}) {
  const name  = (t.name || t._raw?.name || '').toLowerCase();
  const route = (t.launchRoute || t._raw?.launch_route || '').toLowerCase();
  const slug  = (t._raw?.slug || '').toLowerCase();

  // direct matches
  if (route === '/tool/gst-invoice' || slug === 'gst-invoice') return true;

  // heuristic (covers slightly renamed rows)
  return name.includes('gst') && name.includes('invoice');
}

function makeGstFallback() {
  return normalizeTool({
    name:        'GST/HST Rate Lookup & Invoice Generator',
    description: 'Lookup tax rate by state/category & generate a downloadable PDF invoice.',
    category:    'Invoicing',
    icon_url:    '/assets/icons/invoice.svg',
    launch_route:'/tool/gst-invoice',
    is_paid:     false,
    price_in_credits: 0
  });
}

export const useMarketplaceStore = defineStore('marketplace', {
  state: () => ({
    tools:   [],
    loading: false,
    error:   ''
  }),

  actions: {
    async fetchTools() {
      this.loading = true;
      this.error = '';

      try {
        const { data } = await api.get('/tools');
        let normalized = Array.isArray(data) ? data.map(normalizeTool) : [];

        if (HIDE_GST) {
          // Remove GST tool if present
          normalized = normalized.filter((t) => !isGstTool(t));
        } else {
          // Add GST fallback only when not hidden and missing server row
          if (!hasGstTool(normalized)) normalized.push(makeGstFallback());
        }

        this.tools = normalized;
      } catch (err) {
        this.error = err?.response?.data?.error || err?.message || 'Failed to load tools';

        if (HIDE_GST) {
          // When hidden, never surface a fallback
          this.tools = [];
        } else {
          // Keep UI functional with fallback if API fails
          if (!hasGstTool(this.tools)) this.tools = [makeGstFallback()];
        }
      } finally {
        this.loading = false;
      }
    },

    /**
     * Record usage & deduct credits if server-managed; otherwise just return route.
     * @param {Object} tool - normalized tool from state.tools
     * @returns {{ launchRoute: string, launchUrl: string }}
     */
    async launchTool(tool) {
      // Client-only (no id) → skip server usage log, just return route
      if (!tool?.id) {
        return {
          launchRoute: tool.launchRoute || '/tool/gst-invoice',
          launchUrl:   ''
        };
      }

      try {
        const { data } = await api.post(`/tools/${tool.id}/usage`);

        const auth = useAuthStore();
        if (tool.isPaid && toNum(tool.cost) > 0) {
          auth.credits = Math.max(0, toNum(auth.credits) - toNum(tool.cost));
        }

        const launchRoute =
          data.launchRoute ?? data.launch_route ?? tool.launchRoute ?? '';
        const launchUrl = data.launchUrl ?? data.launch_url ?? '';

        return { launchRoute, launchUrl };
      } catch (err) {
        const msg = err?.response?.data?.error || err?.message || 'Error launching tool';
        throw new Error(msg);
      }
    }
  }
});
